import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import interviewService from '../services/interviewService';
import applicationService from '../services/applicationService';

const ScheduleInterviewModal = ({ onClose, onSuccess, preselectedApplicationId = null, preselectedEventType = 'interview' }) => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [formData, setFormData] = useState({
    applicationId: preselectedApplicationId || '',
    scheduledDate: '',
    scheduledTime: '',
    interviewerId: '',
    eventType: preselectedEventType,
    title: '',
    description: '',
    meetingLink: '',
    meetingInstructions: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchApplications();
    fetchInterviewers();
    
    // If we have a preselected application, fetch it directly to ensure it's available
    if (preselectedApplicationId) {
      fetchPreselectedApplication();
    }
  }, []);

  const fetchPreselectedApplication = async () => {
    if (!preselectedApplicationId) return;
    
    try {
      const response = await applicationService.getApplicationById(preselectedApplicationId);
      if (response.success && response.data) {
        // Add the preselected application to the list immediately
        setApplications(prev => {
          const exists = prev.some(app => app._id === preselectedApplicationId);
          if (exists) return prev;
          return [response.data, ...prev];
        });
      }
    } catch (err) {
      console.error('Failed to load preselected application:', err);
    }
  };

  // Refetch applications when event type changes
  useEffect(() => {
    if (formData.eventType) {
      fetchApplications();
    }
  }, [formData.eventType]);

  const fetchApplications = async () => {
    try {
      // Load all paginated applications so dropdown is complete
      const limit = 200;
      const firstPageResponse = await applicationService.getApplications({ page: 1, limit });
      if (firstPageResponse.success) {
        let allApplications = [...(firstPageResponse.applications || [])];
        const totalPages = firstPageResponse.totalPages || 1;

        if (totalPages > 1) {
          const pageRequests = [];
          for (let page = 2; page <= totalPages; page += 1) {
            pageRequests.push(applicationService.getApplications({ page, limit }));
          }

          const pageResponses = await Promise.all(pageRequests);
          pageResponses.forEach((pageResponse) => {
            if (pageResponse.success && Array.isArray(pageResponse.applications)) {
              allApplications = allApplications.concat(pageResponse.applications);
            }
          });
        }

        console.log('All applications loaded:', allApplications.length);
        console.log('Applications by stage:', allApplications.reduce((acc, app) => {
          acc[app.currentStage] = (acc[app.currentStage] || 0) + 1;
          return acc;
        }, {}));

        // For interview events, only show applications in INTERVIEW stage
        // For exam and process events, show ALL applications (maximum flexibility)
        let filteredApps = allApplications;

        // If we have a selected event type, filter accordingly
        if (formData.eventType === 'interview') {
          const interviewEligibleStages = ['INTERVIEW', 'HR_SHORTLISTING', 'APPLICATION'];
          filteredApps = allApplications.filter(
            app => interviewEligibleStages.includes(app.currentStage)
          );
          console.log(`Filtered ${filteredApps.length} applications in interview-eligible stages for interview events`);
        } else {
          // For exam and process events, show ALL applications - no restrictions
          filteredApps = allApplications;
          console.log(`Showing all ${filteredApps.length} applications for ${formData.eventType} events`);
        }

        // If we have a preselected application, ensure it's in the list
        if (preselectedApplicationId) {
          const preselectedExists = filteredApps.some(app => app._id === preselectedApplicationId);
          if (!preselectedExists) {
            const preselectedApp = allApplications.find(app => app._id === preselectedApplicationId);
            if (preselectedApp) {
              filteredApps = [preselectedApp, ...filteredApps];
              console.log('Added preselected application to the list');
            }
          }
        }

        setApplications(filteredApps);
      }
    } catch (err) {
      console.error('Failed to load applications:', err);
    }
  };

  const fetchInterviewers = async () => {
    // Use current user as the interviewer
    if (user?.id) {
      setInterviewers([
        { id: user.id, name: 'Me (Current User)' }
      ]);
      // Set default interviewer to current user
      setFormData(prev => ({ ...prev, interviewerId: user.id }));
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.applicationId) {
      setError('Please select an application');
      return;
    }

    if (!formData.scheduledDate || !formData.scheduledTime) {
      setError('Please select date and time');
      return;
    }

    // interviewerId is now optional

    // Meeting link is optional, but if provided, must be a valid URL
    if (formData.meetingLink && !/^https?:\/\/.+/.test(formData.meetingLink)) {
      setError('Meeting link must be a valid URL (starting with http or https)');
      return;
    }

    // Combine date and time
    const scheduledAt = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);

    // Check if date is in the future
    if (scheduledAt <= new Date()) {
      setError('Interview must be scheduled for a future date/time');
      return;
    }

    setLoading(true);

    try {
      const response = await interviewService.scheduleInterview({
        applicationId: formData.applicationId,
        scheduledAt: scheduledAt.toISOString(),
        interviewerId: formData.interviewerId,
        eventType: formData.eventType,
        title: formData.title,
        description: formData.description,
        meetingLink: formData.meetingLink,
        meetingInstructions: formData.meetingInstructions,
        notes: formData.notes
      });

      if (response.success) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to schedule event');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-5" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.3)] max-w-[600px] w-full max-h-[90vh] flex flex-col md:max-w-full md:m-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-6 py-5 border-b border-[#e9ecef] flex-shrink-0">
          <h2 className="m-0 text-[#2c3e50] text-2xl">Schedule Event</h2>
          <button className="bg-transparent border-none text-[2rem] text-[#6c757d] cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded transition-all duration-200 hover:bg-[#f8f9fa] hover:text-[#2c3e50]" onClick={onClose}>×</button>
        </div>

        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="bg-[#f8d7da] text-[#721c24] px-4 py-3 rounded-md mb-5 border border-[#f5c6cb] text-[0.95rem]">{error}</div>
            )}

          <div className="mb-5">
            <label htmlFor="eventType" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Event Type *</label>
            <select
              id="eventType"
              name="eventType"
              value={formData.eventType}
              onChange={handleChange}
              disabled={loading || preselectedEventType !== 'interview'}
              required
              className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
            >
              <option value="interview">Interview</option>
              <option value="exam">Exam</option>
              <option value="process">Pre-Departure</option>
            </select>
            {preselectedEventType !== 'interview' && (
              <small className="block mt-sm text-text-tertiary text-[13px] leading-[1.4]">
                Event type is pre-selected based on your action
              </small>
            )}
          </div>

          <div className="mb-5">
            <label htmlFor="title" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Event title (optional - will be auto-generated if empty)"
              disabled={loading}
              className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
            />
          </div>

          <div className="mb-5">
            <label htmlFor="description" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Event description..."
              rows={3}
              maxLength={1000}
              disabled={loading}
              className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 resize-y font-[inherit] focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
            />
            <small className="block mt-1 text-[#6c757d] text-[0.85rem]">{formData.description.length}/1000 characters</small>
          </div>

          <div className="mb-5 p-3 border border-[#e9ecef] rounded-md bg-[#f8f9fa]">
            <h4 className="m-0 mb-3 text-[#2c3e50] text-base">Meeting Details</h4>
            <div className="mb-3">
              <label htmlFor="meetingLink" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Meeting Link (Optional)</label>
              <input
                type="url"
                id="meetingLink"
                name="meetingLink"
                value={formData.meetingLink}
                onChange={handleChange}
                placeholder="https://meet.google.com/..."
                disabled={loading}
                className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
              />
            </div>
            <div>
              <label htmlFor="meetingInstructions" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Meeting Instructions</label>
              <textarea
                id="meetingInstructions"
                name="meetingInstructions"
                value={formData.meetingInstructions}
                onChange={handleChange}
                placeholder="Any joining instructions for the candidate..."
                rows={2}
                maxLength={1000}
                disabled={loading}
                className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 resize-y font-[inherit] focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="mb-5">
            <label htmlFor="applicationId" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Application *</label>
            {preselectedApplicationId ? (
              <div>
                <select
                  id="applicationId"
                  name="applicationId"
                  value={formData.applicationId}
                  onChange={handleChange}
                  disabled={true}
                  required
                  className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
                >
                  {applications.length === 0 ? (
                    <option value={preselectedApplicationId}>Loading application...</option>
                  ) : (
                    applications.map(app => (
                      <option key={app._id} value={app._id}>
                        {app.jobPosting} - {app.candidateId?.firstName} {app.candidateId?.lastName}
                      </option>
                    ))
                  )}
                </select>
                <small className="block mt-1 text-[#6c757d] text-[0.85rem]">Application is pre-selected for this event</small>
              </div>
            ) : (
              <select
                id="applicationId"
                name="applicationId"
                value={formData.applicationId}
                onChange={handleChange}
                disabled={loading}
                required
                className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
              >
                <option value="">Select application...</option>
                {applications.map(app => (
                  <option key={app._id} value={app._id}>
                    {app.jobPosting} - {app.candidateId?.firstName} {app.candidateId?.lastName}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-1">
            <div className="mb-5">
              <label htmlFor="scheduledDate" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Date *</label>
              <input
                type="date"
                id="scheduledDate"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleChange}
                min={today}
                disabled={loading}
                required
                className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
              />
            </div>

            <div className="mb-5">
              <label htmlFor="scheduledTime" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Time *</label>
              <input
                type="time"
                id="scheduledTime"
                name="scheduledTime"
                value={formData.scheduledTime}
                onChange={handleChange}
                disabled={loading}
                required
                className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="mb-5">
            <label htmlFor="interviewerId" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Interviewer *</label>
            <select
              id="interviewerId"
              name="interviewerId"
              value={formData.interviewerId}
              onChange={handleChange}
              disabled={loading}
              required
              className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
            >
              <option value="">Select interviewer...</option>
              {interviewers.map(interviewer => (
                <option key={interviewer.id} value={interviewer.id}>
                  {interviewer.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-5">
            <label htmlFor="notes" className="block mb-2 font-medium text-[#495057] text-[0.95rem]">Notes (Optional)</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any notes about the interview..."
              rows={4}
              maxLength={2000}
              disabled={loading}
              className="w-full px-3 py-2.5 border border-[#ced4da] rounded-md text-base transition-[border-color] duration-200 resize-y font-[inherit] focus:outline-none focus:border-[#007bff] focus:shadow-[0_0_0_3px_rgba(0,123,255,0.1)] disabled:bg-[#e9ecef] disabled:cursor-not-allowed"
            />
            <small className="block mt-1 text-[#6c757d] text-[0.85rem]">{formData.notes.length}/2000 characters</small>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-[#e9ecef] md:flex-col-reverse md:[&>button]:w-full">
            <button
              type="button"
              className="px-5 py-2.5 border-none rounded-md text-base font-medium cursor-pointer transition-all duration-200 bg-[#6c757d] text-white hover:bg-[#5a6268] disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 border-none rounded-md text-base font-medium cursor-pointer transition-all duration-200 bg-[#007bff] text-white hover:bg-[#0056b3] disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Scheduling...' : `Schedule ${formData.eventType.charAt(0).toUpperCase() + formData.eventType.slice(1)}`}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduleInterviewModal;
